import React, { MouseEvent as MouseEventHandler, ReactNode, useEffect, useMemo, useState } from "react";
import Button from "../Button";
import './Dropdown.scss';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faSortDown } from "@fortawesome/free-solid-svg-icons";


export interface DropdownOption<T=string> {
    value: T;
    display?: ReactNode;
    hidden?: boolean;
}

interface DropdownProps<T=string> {
    label?: ReactNode;
    items: DropdownOption<T>[];
    onSelect?: (value: T) => void;
    selected?: T;
    position?: 'left' | 'right' | 'top' | 'bottom';
    className?: string;
}

export const Dropdown = <T=string>(props: DropdownProps<T>) => {
    const { label, items, onSelect, selected, position = 'bottom', className } = props;

    const [ open, setOpen ] = useState(false);

    useEffect(() => {
        const closeDropdown = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
        }
        
        if (open) {
            window.addEventListener('click', closeDropdown)
        } else {
            window.removeEventListener('click', closeDropdown)
        }
        
        return () => {
            window.removeEventListener('click', closeDropdown)
        }
    }, [open]);

    const toggleOpen = (e: MouseEventHandler<HTMLButtonElement>) => {
        preventClick(e);
        setOpen(prevOpen => !prevOpen);
    }

    const onClickItem = (e: MouseEventHandler<HTMLButtonElement>, item: T) => {
        preventClick(e);
        setOpen(false);

        if (onSelect) {
            onSelect(item);
        }
    }

    const preventClick = (e: MouseEventHandler<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }

    const displayLabel = useMemo(() => {
        let display = label || 'Select';
        
        if (selected) {
            const selectedItem = items.find(item => item.value === selected);
            
            if (selectedItem) {
                display = selectedItem.display || selectedItem.value;
            }
        }
        
        return display;
    }, [selected, label, items]);
    
    return (
        <div className={'Dropdown' + (className ? ' ' + className : '')}>
            <Button className="Dropdown__trigger" onClick={toggleOpen}>
                <span className="trigger">{displayLabel}</span>
                <FontAwesomeIcon className="icon" icon={faCaretDown} />
            </Button>
            
            {open && <div className={`Dropdown__content ${position}`} onClick={preventClick}>
                {!!items?.length && items.map((item, index) => {
                    if (item.hidden) {
                        return null;
                    }

                    return (
                        <button
                            key={'ddl-' + index}
                            className={'Dropdown__content__item' + (item.value === selected ? ' selected' : '')}
                            onClick={(e) => onClickItem(e, item.value)}
                        >
                            {item.display || item.value}
                        </button>
                    );
                })}
            </div>}
        </div>
    );
}
